import * as React from "react";
import { useState, useCallback, useMemo } from "react";
import { v4 as uuidv4 } from "uuid";
import Routes from "routing/Routes";
import { qualifyUrl } from "util/URLUtils";
import { IfPermitted, HasOwnership } from "components/common";
import EnterpriseApiRoutes from "common/ApiRoutes";
import useScopePermissions from "hooks/useScopePermissions";
import { useModal, ConfirmDialog } from "security-app/components/common/Modal";
import { useDeleteSigmaRule } from "security-app/hooks/useSigmaAPI";
import { useSelectedRowsDispatch } from "common/contexts";
import { MenuItem, DeleteMenuItem } from "components/bootstrap";
import EntityShareModal from "components/permissions/EntityShareModal";
import usePluggableEntitySharedActions from "hooks/usePluggableEntitySharedActions";
import { MoreActionsMenu } from "components/common/MoreActions";
function RowActions({ rule, editRule }) {
  const { deleteSigmaRule } = useDeleteSigmaRule();
  const selectedRulesDispatch = useSelectedRowsDispatch();
  const [showEntityShareModal, setShowEntityShareModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useModal();
  const { scopePermissions } = useScopePermissions(rule);
  const isMutable = useMemo(() => scopePermissions == null ? void 0 : scopePermissions.is_mutable, [scopePermissions]);
  const { actions: pluggableActions, actionModals: pluggableActionModals } = usePluggableEntitySharedActions(rule, "sigma_rule");
  const moreActions = [pluggableActions.length ? pluggableActions : null].filter(Boolean);
  const toggleEntityShareModal = useCallback(() => {
    setShowEntityShareModal((cur) => !cur);
  }, []);
  const onEdit = () => {
    editRule(rule);
  };
  const onClone = () => {
    const auxRule = { ...rule, id: null, _scope: "DEFAULT" };
    auxRule.source = auxRule.source.replace(/title: .+/, `title: CLONE - ${rule.title}`).replace(/id: .+/, `id: ${uuidv4()}`);
    editRule(auxRule);
  };
  const onDownload = () => {
    window.open(qualifyUrl(EnterpriseApiRoutes.SecurityApp.downloadSigmaRule(`ruleIds=${rule.id}`).url), "_self");
  };
  const onEditEventDefinition = () => {
    window.open(Routes.ALERTS.DEFINITIONS.edit(rule.event_definition_id), "_blank");
  };
  const getRuleTimeRangeSeconds = (rangeNumber, rangeUnit) => {
    let rangeValue = rangeNumber;
    if (["MINUTES", "HOURS"].includes(rangeUnit)) rangeValue *= 60;
    if (rangeUnit === "HOURS") rangeValue *= 60;
    return rangeValue;
  };
  const onSearchLogs = () => {
    if (rule.query) {
      const relativeSeconds = getRuleTimeRangeSeconds(rule.search_within, rule.search_within_unit);
      window.open(
        Routes.search_with_query(
          rule.query,
          "relative",
          { relative: relativeSeconds },
          rule.streams,
          rule.stream_categories
        ),
        "_blank"
      );
    } else if (rule.use_event_definition_replay) {
      window.open(Routes.ALERTS.DEFINITIONS.replay_search(rule.event_definition_id), "_blank");
    }
  };
  const onDelete = () => {
    setShowDeleteConfirm(true);
  };
  const onConfirmedDelete = () => {
    deleteSigmaRule(
      { ruleId: rule.id },
      { onSuccess: () => selectedRulesDispatch({ type: "remove", payload: [rule] }) }
    );
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(MoreActionsMenu, { title: "Toggle rule actions" }, (rule.query || rule.use_event_definition_replay) && /* @__PURE__ */ React.createElement(MenuItem, { "data-testid": "rule-action-search-log", onClick: onSearchLogs }, "Search Logs"), /* @__PURE__ */ React.createElement(IfPermitted, { permissions: `sigma_rules:edit:${rule.id}` }, /* @__PURE__ */ React.createElement(MenuItem, { onClick: onEdit }, "Edit"), /* @__PURE__ */ React.createElement(MenuItem, { onClick: onClone }, "Clone"), /* @__PURE__ */ React.createElement(HasOwnership, { id: rule.id, type: "sigma_rule" }, ({ disabled: hasMissingPermissions }) => /* @__PURE__ */ React.createElement(MenuItem, { disabled: hasMissingPermissions, onClick: toggleEntityShareModal }, "Share")), /* @__PURE__ */ React.createElement(MenuItem, { onClick: onDownload }, "Download"), /* @__PURE__ */ React.createElement(MenuItem, { onClick: onEditEventDefinition }, "Edit Event Definition"), moreActions, isMutable && /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(MenuItem, { divider: true }), /* @__PURE__ */ React.createElement(DeleteMenuItem, { onClick: onDelete })))), /* @__PURE__ */ React.createElement(
    ConfirmDialog,
    {
      show: showDeleteConfirm,
      onConfirm: onConfirmedDelete,
      confirmText: "Delete rule",
      onCancel: () => setShowDeleteConfirm(false),
      title: "Delete rule"
    },
    /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("p", null, "Are you sure you want to delete the following rule?:"), /* @__PURE__ */ React.createElement("p", null, rule.title))
  ), showEntityShareModal && /* @__PURE__ */ React.createElement(
    EntityShareModal,
    {
      entityId: rule.id,
      entityType: "sigma_rule",
      entityTitle: rule.title,
      showShareableEntityURL: false,
      description: "Search for a User or Team to add as collaborator on this sigma rule.",
      onClose: toggleEntityShareModal
    }
  ), pluggableActionModals);
}
export default RowActions;
